// components/SurveyComponent/SurveyComponent.styles.ts
import styled from "@emotion/styled";
import { Box, Checkbox, Radio, Typography } from "@mui/material";
import { mediumMobile, mobile, tablet } from "commons/styled.components";

export const SurveyContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  gap: 1.5rem;
  max-width: 50.5rem;
  width: 100%;
  align-self: center;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  box-shadow: 2px 0px 20px rgba(0, 0, 0, 0.04);
  border-radius: 0.25rem;

  ${tablet(`
    padding: 0.75rem;
    gap: 1rem;
  `)}

  ${mediumMobile(`
    padding: 0.5rem;
    gap: 0.75rem;
  `)}
`;

export const NumberOfQuestions = styled(Typography)`
  /* Question 10/10 */
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #000000;
  align-self: stretch;

  ${tablet(`
    font-size: 0.875rem;
  `)}

  ${mediumMobile(`
    font-size: 0.75rem;
  `)}
`;

export const OptionBox = styled(Box)<{ hasError: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 0.75rem 0.625rem;
  gap: 0.5rem;
  isolation: isolate;

  max-width: 48.5rem;
  min-height: 3.75rem;
  height: auto;

  flex: 1;
  border-radius: 0.25rem;
  border: 1px solid ${(props) => (props.hasError ? "#AF4B4B" : "#e4e4e4")};
  width: 100%;

  ${tablet(`
    padding: 0.625rem 0.5rem;
    min-height: 3.125rem;
  `)}

  ${mediumMobile(`
    padding: 0.5rem 0.375rem;
    min-height: 2.75rem;
  `)}
`;

export const CheckBox = styled(Checkbox)`
  /* Radio button */

  width: 18px;
  height: 18px;

  border-radius: 4px;
  color: #158a73;
  &.Mui-checked {
    color: #158a73;
  }
`;

export const RadioBox = styled(Radio)`
  /* Radio button */

  width: 18px;
  height: 18px;

  border-radius: 4px;
  color: #158a73;
  &.Mui-checked {
    color: #158a73;
  }
`;

export const QuestionTitle = styled(Typography)`
  /* Question Title */
  font-style: normal;
  font-weight: 500;
  font-size: 1.25rem;
  line-height: 120%;
  color: #000000;
  text-align: left;
  width: 100%;

  ${tablet(`
    font-size: 1.125rem;
  `)}

  ${mediumMobile(`
    font-size: 1rem;
  `)}
`;

export const ErrorText = styled(Typography)`
  color: #af4b4b;
  margin-top: 0.5rem;
  text-align: left;
  width: 100%;
  font-size: 0.75rem;
`;

export const SurveyQuestionMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 100%;
  padding: 0 1rem;
  box-sizing: border-box;

  ${tablet(`
    gap: 1rem;
    padding: 0 0.75rem;
  `)}

  ${mediumMobile(`
    gap: 0.75rem;
    padding: 0 0.5rem;
  `)}
`;

export const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 0.5rem;

  min-height: 3.75rem;
  height: auto;
  width: 100%;

  ${mediumMobile(`
    gap: 0.25rem;
  `)}
`;

export const RewardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  min-height: 1.7rem;
  height: auto;
  align-self: stretch;

  ${mediumMobile(`
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  `)}
`;

export const RewardTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
  width: 100%;
  gap: 5px;

  ${mediumMobile(`
    justify-content: flex-start;
    width: auto;
  `)}
`;

export const RewardText = styled(Typography)`
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 140%;
  color: #000000;
  text-align: right;

  ${mediumMobile(`
    width: auto;
    text-align: left;
  `)}
`;

export const CategoryTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 0.5rem;

  margin: 0 auto;
  min-height: 1.7rem;
  height: auto;
  width: 100%;

  ${mediumMobile(`
    margin: 0;
  `)}
`;

export const CategoryText = styled(Typography)`
  min-height: 1.7rem;
  height: auto;
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  color: #000000;

  ${tablet(`
    font-size: 1rem;
  `)}

  ${mediumMobile(`
    font-size: 0.875rem;
  `)}
`;

export const CategoryIcon = styled.img`
  width: 1.5rem;
  height: 1.5rem;

  ${mediumMobile(`
    width: 1.25rem;
    height: 1.25rem;
  `)}
`;

export const RemainingQuestions = styled(Typography)<{ status?: string }>`
  /* Remaining Questions */

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0.25rem 0.375rem;
  gap: 0.5rem;

  min-height: 1.5625rem;
  height: auto;

  background: ${(props) => {
    switch (props.status) {
      case "New":
        return "#F4EDF7";
      case "Started":
        return "#F7F7E8";
      case "Completed":
        return "#E6F1E4";
      default:
        return "#F4EDF7"; // Default color (same as 'New')
    }
  }};
  border-radius: 0.25rem;

  font-style: normal;
  font-weight: 500;
  font-size: 0.75rem;

  color: ${(props) => {
    switch (props.status) {
      case "New":
        return "#954BAF";
      case "Started":
        return "#B1AB1D";
      case "Completed":
        return "#0A7C00";
      default:
        return "#954BAF"; // Default color (same as 'New')
    }
  }};

  ${mediumMobile(`
    font-size: 0.625rem;
    padding: 0.1875rem 0.3125rem;
  `)}
`;

export const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${mobile(`
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  `)}
`;

export const ResponseMsg = styled(Typography)`
  /* Note: Maximum of 50 responses allowed per day. */

  font-weight: 500;
  font-size: 0.875rem;
  color: #727272;

  ${mobile(`
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  `)}
`;

export const ProgressBarContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
  height: 0.5rem;
  background-color: #e0e0e0;
  border-radius: 0.3125rem;
  position: relative;
  overflow: hidden;

  ${tablet(`
    margin-top: 0.75rem;
    margin-bottom: 1.25rem;
  `)}

  ${mediumMobile(`
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    height: 0.375rem;
  `)}
`;

export const ProgressBarFill = styled.div<{ progress: number }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${(props) => `${props.progress}%`};
  background-color: #158a73;
  border-radius: 0.3125rem;
  transition: width 0.3s ease;
`;
